import type { Ref } from 'vue';
import type { SaveDraftMode } from '@/components/dialogs/SaveJobDraftDialog.vue';
import type { AfterBook } from '@/composables/jobBookings/useJobBookingSubmit';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingStop,
  JobBookingCustomer,
} from '@/models/advancedJobBooking';
import type { PacProduct } from '@/models/pacJobBooking';

import { ref } from 'vue';
import { useRouter } from 'vue-router';

import useJobBookingCustomerData, {
  type RefConfigTuple,
  createBookingStop,
} from '@/composables/data/useJobBookingCustomerData';
import useJobBookingSaveDraft from '@/composables/jobBookings/useJobBookingSaveDraft';
import useJobBookingSubmit from '@/composables/jobBookings/useJobBookingSubmit';
import { fetchPostPacJobBookApi } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { createEmptyCustomer } from '@/models/advancedJobBooking';
import {
  buildPacBookApiBody,
  createEmptyPacPalletProduct,
} from '@/models/pacJobBooking';

export interface PacPalletJobBookingViewComposable {
  customer: Ref<JobBookingCustomer>;
  stops: Ref<BookingStop[]>;
  products: Ref<PacProduct[]>;
  attachments: Ref<File[]>;
  refConfigs: Ref<RefConfigTuple>;
  addressItems: Ref<openapi.AddressGETOpenapi[]>;
  addressLoading: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  bookingErrors: Ref<string[]>;
  draftId: Ref<number | null>;
  draftName: Ref<string>;
  addProduct: () => void;
  removeProduct: (index: number) => void;
  submit: (after?: AfterBook) => Promise<boolean>;
  saveDraft: (name: string, mode?: SaveDraftMode) => Promise<void>;
  goToDraftsList: () => void;
  reset: () => void;
}

export default function usePacPalletJobBookingView(): PacPalletJobBookingViewComposable {
  const router = useRouter();
  const { customer, refConfigs, addressItems, addressLoading } =
    useJobBookingCustomerData();

  const stops = ref<BookingStop[]>([
    createBookingStop(1),
    createBookingStop(2),
  ]);
  const products = ref<PacProduct[]>([createEmptyPacPalletProduct()]);
  const attachments = ref<File[]>([]);
  const isSubmitting = ref(false);
  const bookingErrors = ref<string[]>([]);
  const draftId = ref<number | null>(null);
  const draftName = ref('');

  function addProduct(): void {
    products.value.push(createEmptyPacPalletProduct());
  }

  function removeProduct(index: number): void {
    products.value.splice(index, 1);
  }

  function reset(): void {
    customer.value = createEmptyCustomer();
    stops.value = [createBookingStop(1), createBookingStop(2)];
    products.value = [createEmptyPacPalletProduct()];
    attachments.value = [];
    bookingErrors.value = [];
    draftId.value = null;
    draftName.value = '';
  }

  const { submit } = useJobBookingSubmit({
    stops,
    attachments,
    isSubmitting,
    bookingErrors,
    reset,
    router,
    postBooking: (signal) =>
      fetchPostPacJobBookApi(
        buildPacBookApiBody(customer.value, stops.value, products.value),
        { signal },
      ),
  });

  const { saveDraft } = useJobBookingSaveDraft({
    draftId,
    draftName,
    buildContent: () =>
      buildPacBookApiBody(customer.value, stops.value, products.value),
  });

  function goToDraftsList(): void {
    void router.push({ name: routeNames.DRAFT_JOB_BOOKING });
  }

  return {
    customer,
    stops,
    products,
    attachments,
    refConfigs,
    addressItems,
    addressLoading,
    isSubmitting,
    bookingErrors,
    draftId,
    draftName,
    addProduct,
    removeProduct,
    submit,
    saveDraft,
    goToDraftsList,
    reset,
  };
}
